import React from 'react';
import {Atom, AtomRenderProps} from '@packages/systems/wfdl';
import {Map} from 'immutable';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import withAttribute from './withAttribute';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_CHECKOUT_BILLING_ADDRESS_WRAPPER,
} from '@packages/systems/commerce/constants';
import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getBooleanOrElse,
  getChildren,
} from '@packages/utilities/data-utils';

const styleDisplayNone = Map({style: Map({display: 'none'})});

const shouldThisComponentBeShown = ({
  runtimeEnvironment,
  isBillingAddressOpen,
}: {
  runtimeEnvironment: Environment;
  isBillingAddressOpen: boolean;
}) => {
  const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
  if (isDesigner) {
    return isBillingAddressOpen;
  }
  return false;
};

const CommerceCheckoutBillingAddressWrapper = Atom.createRender(
  (props: AtomRenderProps) => {
    const {
      attributes,
      classes,
      context,
      data,
      generateAttributes,
      renderChild,
    } = props;

    const runtimeEnvironment = useRuntimeEnvironment();
    const isBillingAddressOpen: boolean = getBooleanOrElse(
      context.get('isBillingAddressOpen'),
      false
    );

    const styledAttributes = shouldThisComponentBeShown({
      runtimeEnvironment,
      isBillingAddressOpen,
    })
      ? attributes
      : attributes.merge(styleDisplayNone);
    return (
      <form {...generateAttributes(styledAttributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child)
        )}
      </form>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Billing Address',
  showEmpty: true,
  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_CHECKOUT_BILLING_ADDRESS_WRAPPER
  )(CommerceCheckoutBillingAddressWrapper),
  constraints: {
    descendants: [
      {is: 'CommerceCheckoutBillingFullName', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutBillingCity', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutBillingStateProvince', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutBillingStreetAddress', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutBillingStreetAddressOptional', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutBillingZipPostalCode', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutBillingCountrySelector', rule: 'ExactlyOne'},
    ],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          marginTop: px(16),
          marginBottom: px(20),
        },
      },
    },
  },
  fields: {children: TListOfElement},
});

export default config;
